// src/views/PlacementTestView.jsx
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { usePlacementTest } from '../hooks/usePlacementTest';
import PlacementResultView from '../components/placement/PlacementResultView';
import TimerBar from '../components/placement/TimerBar';

export default function PlacementTestView({ mode, onExit, onSaveResult, onGoToDiploma, userName }) {
  const {
    currentQ, phase, lastCorrect,
    questionNumber, totalQuestions,
    finalLevel, skillScores, totalCorrect,
    timeLeft, totalTime, accuracy,
    answer, onAudioReady, currentAudioUrl,
  } = usePlacementTest(mode);

  const audioRef = useRef(null);
  const [audioPlayed, setAudioPlayed]   = useState(false);
  const [audioError,  setAudioError]    = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // ── Reproducir audio UNA SOLA VEZ cuando phase === 'audio' ──────────────────
  useEffect(() => {
    if (phase !== 'audio' || !currentAudioUrl) return;

    // Reset estado por cada nueva pregunta
    setAudioPlayed(false);
    setAudioError(false);
    setAudioPlaying(false);

    const audio = new Audio(currentAudioUrl);
    audioRef.current = audio;

    audio.onplay  = () => setAudioPlaying(true);
    audio.onended = () => {
      setAudioPlayed(true);
      setAudioPlaying(false);
      onAudioReady(); // ← timer arranca aquí
    };
    audio.onerror = () => {
      setAudioError(true);
      setAudioPlaying(false);
      onAudioReady(); // si falla el audio, continuar igual
    };

    // Pequeño delay para que el navegador no bloquee autoplay
    const t = setTimeout(() => audio.play().catch(() => {
      setAudioError(true);
      onAudioReady();
    }), 300);

    return () => {
      clearTimeout(t);
      audio.pause();
      audio.src = '';
    };
  }, [phase, currentAudioUrl]); // ← solo cuando cambia la pregunta de audio

  // ── Fin del test ─────────────────────────────────────────────────────────────
  if (phase === 'finished') {
    return (
      <PlacementResultView
        mode={mode}
        finalLevel={finalLevel}
        skillScores={skillScores}
        totalCorrect={totalCorrect}
        totalQuestions={totalQuestions}
        accuracy={accuracy}
        totalTime={totalTime}
        userName={userName}
        onExit={onExit}
        onSaveResult={onSaveResult}
        onGoToDiploma={onGoToDiploma}
        onStartMaster={onExit}
      />
    );
  }

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Cargando pregunta...</p>
      </div>
    );
  }

  const isReading   = currentQ.type === 'reading';
  const isListening = currentQ.type === 'listening';
  const totalSecs   = isReading ? 50 : isListening ? 45 : 20;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col p-4 gap-5">

      {/* HEADER */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onExit}
          className="p-3 rounded-full bg-white border-2 border-slate-100 text-slate-400 hover:text-indigo-600 transition-all shadow-sm active:scale-95"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <div className="flex-1 bg-white rounded-2xl px-4 py-2 border-2 border-indigo-50 shadow-sm">
          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
            {mode === 'master' ? 'Master Challenge' : 'Placement Test'}
          </p>
          <p className="text-xs text-slate-500">Pregunta {questionNumber} de {totalQuestions}</p>
        </div>
      </div>

      {/* PROGRESO */}
      <div className="w-full max-w-md mx-auto">
        <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-500 transition-all duration-300"
            style={{ width: `${((questionNumber - 1) / totalQuestions) * 100}%` }}
          />
        </div>
        <p className="text-[11px] text-slate-400 font-medium mt-1 text-center">
          {Math.round(((questionNumber - 1) / totalQuestions) * 100)}% completado
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center gap-4 mt-2">

        {/* TIMER — solo visible en fase testing */}
        {phase === 'testing' && (
          <div className="w-full max-w-md">
            <TimerBar timeLeft={timeLeft} totalSeconds={totalSecs} />
          </div>
        )}

        {/* ── TARJETA AUDIO (fase 'audio') ── */}
        {isListening && phase === 'audio' && (
          <div className="w-full max-w-md bg-indigo-900 rounded-3xl shadow-2xl p-6 border-2 border-indigo-700 text-center space-y-4">
            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">
              🎧 Listening
            </p>
            <div className="flex flex-col items-center gap-3">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4
                ${audioPlaying
                  ? 'bg-indigo-500 border-indigo-300 animate-pulse'
                  : audioPlayed
                  ? 'bg-green-600 border-green-400'
                  : 'bg-indigo-700 border-indigo-500'
                }`}
              >
                {audioError
                  ? <VolumeX size={32} className="text-red-300" />
                  : <Volume2 size={32} className="text-white" />
                }
              </div>
              <p className="text-white font-bold text-sm">
                {audioError   ? 'Error al cargar el audio' :
                 audioPlaying ? 'Reproduciendo...' :
                 audioPlayed  ? '✓ Audio completado' :
                 'Preparando audio...'}
              </p>
              {!audioError && !audioPlayed && (
                <p className="text-indigo-300 text-xs">
                  Escucha con atención — solo se reproduce una vez
                </p>
              )}
              {audioError && (
                <button
                  onClick={onAudioReady}
                  className="mt-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black border-b-2 border-indigo-800 active:border-b-0 transition-all"
                >
                  Continuar de todas formas
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── TEXTO READING ── */}
        {isReading && (
          <div className="w-full max-w-md bg-white rounded-3xl shadow-md p-4 border border-indigo-50">
            <p className="text-[11px] font-black text-indigo-400 uppercase tracking-widest mb-2">Reading</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{currentQ.passage}</p>
          </div>
        )}

        {/* ── TARJETA PREGUNTA — oculta durante fase audio ── */}
        {phase !== 'audio' && (
          <div className={`w-full max-w-md bg-white rounded-3xl shadow-xl p-5 border-2 transition-all duration-300
            ${lastCorrect === true  ? 'border-emerald-400 shadow-emerald-100' : ''}
            ${lastCorrect === false ? 'border-red-400 shadow-red-100'         : ''}
            ${lastCorrect === null  ? 'border-indigo-50'                      : ''}
          `}>
            {lastCorrect === true  && <p className="text-emerald-500 font-black text-xs mb-1">✅ ¡Correcto!</p>}
            {lastCorrect === false && <p className="text-red-500 font-black text-xs mb-1">❌ Incorrecto</p>}

            <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-2">
              {currentQ.skill.toUpperCase()}
            </p>
            <p className="text-base font-bold text-slate-800 mb-3">{currentQ.q}</p>

            <div className="grid grid-cols-1 gap-2">
              {currentQ.options.map((opt, idx) => {
                const isCorrectOpt    = opt === currentQ.a;
                const isSelectedWrong = phase === 'feedback' && lastCorrect === false && opt !== currentQ.a;
                let style = 'border-slate-100 bg-white text-slate-700 hover:border-indigo-300 hover:shadow-md active:scale-95';
                if (phase === 'feedback') {
                  if (isCorrectOpt)         style = 'border-emerald-400 bg-emerald-50 text-emerald-700';
                  else if (isSelectedWrong) style = 'border-red-300 bg-red-50 text-red-500';
                  else                      style = 'border-slate-100 bg-slate-50 text-slate-400';
                }
                return (
                  <button
                    key={idx}
                    disabled={phase !== 'testing'}
                    onClick={() => answer(opt)}
                    className={`w-full text-left px-3 py-3 rounded-2xl border-2 text-sm font-semibold shadow-sm transition-all ${style}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
